import React, { useEffect, useRef, useCallback } from 'react';
import { Calendar, Mic } from 'lucide-react';
import { ANIMAL_ICONS } from '../../theme';
import { ImperialPulseCSS, ImperialProgressBar } from './ImperialProgressBar';

/* ═══════════════════════════════════════════════════════════════
   Single-upload "Add Details" step — now uses the shared
   Imperial progress bar so it matches the batch/multi flow.
   ═══════════════════════════════════════════════════════════════ */

/* ─── PRELOAD all 7 icons on mount ─── */
const usePreloadIcons = () => {
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    Object.values(ANIMAL_ICONS).forEach(({ src }) => { const i = new Image(); i.src = src; });
  }, []);
};

/* ─── MAIN COMPONENT ─── */
export const DetailsStep = ({
  mediaType, croppedPreview, filePreview, fileName,
  note, onNoteChange, vibeDate, onDateChange,
  uploading, uploadPercent, fileBlob, onSubmit, accentColor, category,
  onHunterFinished,
}) => {
  usePreloadIcons();

  const formatDateInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
    return formatted;
  };

  /* Trigger roar immediately when real upload hits 100% */
  const firedRef = useRef(false);
  const cbRef = useRef(onHunterFinished);
  cbRef.current = onHunterFinished;

  useEffect(() => {
    if (!uploading) { firedRef.current = false; return; }
    if (uploadPercent >= 100 && !firedRef.current) {
      firedRef.current = true;
      if (cbRef.current) cbRef.current();
    }
  }, [uploading, uploadPercent]);

  const icon = ANIMAL_ICONS[category] || ANIMAL_ICONS.legacy;

  return (
    <div className="flex-1 overflow-y-auto p-5 space-y-4">
      <ImperialPulseCSS />

      {/* Media preview */}
      <div className="rounded-xl overflow-hidden border border-[#D4AF37]/10" style={{ background: '#000' }}>
        {mediaType === 'image' && croppedPreview && (
          <img src={croppedPreview} alt="Preview" className="w-full max-h-[200px] object-contain" data-testid="cropped-preview" />
        )}
        {mediaType === 'video' && filePreview && (
          <video src={filePreview} controls className="w-full max-h-[200px]" data-testid="video-preview" />
        )}
        {mediaType === 'audio' && filePreview && (
          <div className="p-6 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: accentColor || '#D4AF37' }}>
              <Mic size={24} className="text-black" />
            </div>
            <p className="font-['Outfit'] text-sm text-white/40">{fileName}</p>
            <audio src={filePreview} controls className="w-full" data-testid="audio-preview" />
          </div>
        )}
      </div>

      {/* Note */}
      <div>
        <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Vibe Note</label>
        <textarea value={note} onChange={(e) => onNoteChange(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-[#D4AF37]/15 bg-white/5 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 font-['Outfit'] text-white/90 placeholder-white/20 resize-none"
          placeholder="Share your vibe..." rows="3" data-testid="upload-note-input" />
      </div>

      {/* Date */}
      <div className="flex justify-end">
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/15 bg-white/5 hover:border-[#D4AF37]/30 transition-colors"
          style={{ maxWidth: '170px' }}>
          <Calendar size={13} className="text-[#D4AF37] flex-shrink-0" />
          <input type="text" value={vibeDate}
            onChange={(e) => onDateChange(formatDateInput(e.target.value))}
            placeholder="DD/MM/YYYY" maxLength={10}
            className="w-full bg-transparent text-xs font-['Outfit'] text-white/80 placeholder:text-white/20 outline-none"
            style={{ fontStyle: 'italic', letterSpacing: '0.04em' }}
            data-testid="vibe-date-input" />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          IMPERIAL PULSE — Oscillating beast + breathing glow
          Real upload progress. Roar on actual completion.
          ═══════════════════════════════════════════════════════ */}
      {uploading && (
        <ImperialProgressBar
          icon={icon}
          uploadPercent={uploadPercent}
          caption={`${fileBlob ? `${(fileBlob.size / (1024 * 1024)).toFixed(1)} MB` : ''}${uploadPercent < 100 ? ' \u2014 the beast hunts...' : ' \u2014 captured.'}`}
        />
      )}

      {/* Submit */}
      <button onClick={onSubmit} disabled={uploading}
        className="w-full py-3 rounded-full font-['Outfit'] font-semibold transition-all disabled:opacity-50"
        style={{
          background: uploading ? 'transparent' : (accentColor || '#D4AF37'),
          color: uploading ? 'rgba(212,175,55,0.5)' : '#000',
          border: uploading ? '1px solid rgba(212,175,55,0.2)' : 'none',
          boxShadow: uploading ? 'none' : '0 0 20px rgba(212,175,55,0.25)',
        }}
        data-testid="upload-submit-button">
        {uploading ? 'The hunt is on...' : 'Add Vibe'}
      </button>
    </div>
  );
};
